import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Avatar,
  Text,
  Input,
  Button,
  IconButton,
  Link as ChakraLink,
  useToast,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
} from '@chakra-ui/react';
import { AttachmentIcon, DeleteIcon, EditIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import { likeOrDislikeComment, likeOrDislikeReply } from '../api/taskApi';
import { Editor } from '@tinymce/tinymce-react';
import { useTasks } from '../hooks/useTasks';
import { TaskMentionDisplay } from './TaskMentionDisplay';
import { findTaskByIdentifier } from '../utils/taskLinking';
import { usePermissions } from './PermissionGuard';

const getAuthorId = (author: string | { _id: string }) =>
  typeof author === 'string' ? author : author?._id;

const isCommentAuthor = (userId: string | undefined, author: string | { _id: string }) =>
  !!userId && userId === getAuthorId(author);

const formatCommentTimestamp = (item: { createdAt: string; updatedAt?: string }) => {
  const edited = item.updatedAt && new Date(item.updatedAt).getTime() > new Date(item.createdAt).getTime() + 1000;
  const displayDate = edited ? item.updatedAt! : item.createdAt;
  return {
    label: new Date(displayDate).toLocaleString(),
    edited,
  };
};

// testing
interface CommentsSectionProps {
  comments: any[];
  users: Record<string, any>;
  addCommentMutation: any;
  deleteCommentMutation: any;
  refetchComments: () => void;
  addReplyMutation?: any;
  deleteReplyMutation?: any;
  editReplyMutation?: any;
  user?: any;
  taskId: string; // Add taskId as a required prop
}

interface AIRequest {
  request: any;
  respondWith: {
    string: (callback: () => Promise<string>) => void;
  };
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  comments,
  users,
  addCommentMutation,
  deleteCommentMutation,
  refetchComments,
  addReplyMutation,
  deleteReplyMutation,
  editReplyMutation,
  user,
  taskId, // Add taskId to destructured props
}) => {
  const { canPerformWrites } = usePermissions();
  // Fetch all tasks for mentions
  const { data: allTasks } = useTasks(true);
  
  const [commentImages, setCommentImages] = useState<string[]>([]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyImages, setReplyImages] = useState<string[]>([]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [likeLoading, setLikeLoading] = useState<{ [id: string]: boolean }>({});
  const [mainEditorContent, setMainEditorContent] = useState<string>('');
  const [replyEditorContents, setReplyEditorContents] = useState<{ [id: string]: string }>({});

  // Helper function to create TinyMCE setup with task mentions
  const createEditorSetup = () => ({
    height: 150,
    menubar: false,
    plugins: ['link', 'lists', 'code', 'autolink', 'paste'],
    toolbar: 'undo redo | bold italic | link | bullist numlist | removeformat',
    branding: false,
    statusbar: false,
    setup: (editor: any) => {
      let isProcessing = false;
      
      // Helper function to convert task mentions to clickable links
      const convertTaskMentions = (content: string) => {
        if (!allTasks) return content;
        
        const taskMentionPattern = /(?:#([A-Z]{2,}-[A-Z0-9-]+)|@task:([a-zA-Z0-9]+)|([A-Z]{2,}-[A-Z0-9-]+))/g;
        
        return content.replace(taskMentionPattern, (match, hashTaskMatch, mongoIdMatch, plainTaskMatch) => {
          let taskId: string;
          let type: 'taskId' | 'mongoId' | 'projectNumber';
          
          if (hashTaskMatch) {
            taskId = hashTaskMatch;
            type = 'taskId';
          } else if (mongoIdMatch) {
            taskId = mongoIdMatch;
            type = 'mongoId';
          } else if (plainTaskMatch) {
            taskId = plainTaskMatch;
            type = 'projectNumber';
          } else {
            return match;
          }
          
          const task = findTaskByIdentifier(allTasks, taskId, type);
          
          if (!task) {
            return `<span class="task-mention-not-found" style="color: #999; text-decoration: line-through; background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px;" title="Task not found">${match}</span>`;
          }
          
          return `<span class="task-mention" style="background-color: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; cursor: pointer; text-decoration: none; font-weight: 500;" data-task-id="${task._id}" title="${task.taskTitle} (${task.status})">${match}</span>`;
        });
      };
      
      // Auto-detect task mentions when user stops typing
      let typingTimer: NodeJS.Timeout;
      editor.on('keyup', () => {
        if (isProcessing) return;
        
        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
          isProcessing = true;
          const content = editor.getContent();
          const newContent = convertTaskMentions(content);
          
          if (newContent !== content) {
            const cursorPos = editor.selection.getBookmark();
            editor.setContent(newContent);
            editor.selection.moveToBookmark(cursorPos);
          }
          isProcessing = false;
        }, 1000);
      });
      
      // Handle clicks on task mentions
      editor.on('click', (e: any) => {
        const target = e.target;
        if (target.classList.contains('task-mention')) {
          const taskId = target.getAttribute('data-task-id');
          if (taskId) {
            window.open(`/dashboard/task/${taskId}`, '_blank');
          }
        }
      });
    },
  });
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Helper function to handle image preview
  const handleImageClick = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    onOpen();
  };

  // Hash userId to color for reply box border
  function userColor(userId: string) {
    if (!userId) return '#3182ce';
    let hash = 0;
    for (let i = 0; i < userId.length; i++) hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    const c = (hash & 0x00ffffff).toString(16).toUpperCase();
    return '#' + '00000'.substring(0, 6 - c.length) + c;
  }

  // Replies are flat — one level under each comment (no reply-to-reply).
  const organizeReplies = (replies: any[]) => {
    if (!replies || replies.length === 0) return [];

    return [...replies].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  };

  // Helper function to format user names for tooltips
  const formatUserNames = (userIds: string[]) => {
    if (!userIds || userIds.length === 0) return '';
    return userIds.map(userId => {
      const user = users[userId];
      return user ? `${user.firstName} ${user.lastName}` : userId;
    }).join(', ');
  };

  // Helper function to get tooltip label
  const getLikeTooltip = (userIds: string[], action: 'like' | 'dislike') => {
    if (!userIds || userIds.length === 0) {
      return `No ${action}s yet`;
    }
    const names = formatUserNames(userIds);
    const count = userIds.length;
    const actionText = action === 'like' ? 'liked' : 'disliked';
    return count === 1 ? `${names} ${actionText} this` : `${count} users ${actionText} this: ${names}`;
  };

  // Like/dislike handlers
  const handleLikeDislikeComment = async (commentId: string, action: 'like' | 'dislike') => {
    if (!user) {
      toast({ title: 'Please log in to like/dislike comments', status: 'warning', duration: 3000 });
      return;
    }
    setLikeLoading(l => ({ ...l, [commentId]: true }));
    try {
      await likeOrDislikeComment(taskId, commentId, action); // Use taskId prop instead of comments[0].taskId
      refetchComments();
    } catch (error: any) {
      console.error('Error liking/disliking comment:', error);
      toast({ 
        title: 'Error updating like/dislike', 
        description: error.response?.data?.message || error.message || 'Please try again',
        status: 'error', 
        duration: 3000 
      });
    }
    setLikeLoading(l => ({ ...l, [commentId]: false }));
  };
  const handleLikeDislikeReply = async (commentId: string, replyId: string, action: 'like' | 'dislike') => {
    if (!user) {
      toast({ title: 'Please log in to like/dislike replies', status: 'warning', duration: 3000 });
      return;
    }
    setLikeLoading(l => ({ ...l, [replyId]: true }));
    try {
      await likeOrDislikeReply(taskId, commentId, replyId, action); // Use taskId prop instead of comments[0].taskId
      refetchComments();
    } catch (error: any) {
      console.error('Error liking/disliking reply:', error);
      toast({ 
        title: 'Error updating like/dislike', 
        description: error.response?.data?.message || error.message || 'Please try again',
        status: 'error', 
        duration: 3000 
      });
    }
    setLikeLoading(l => ({ ...l, [replyId]: false }));
  };
  // TinyMCE refs
  const mainCommentEditorRef = useRef<any>(null);
  const replyEditorRefs = useRef<Record<string, any>>({});
  const editCommentEditorRef = useRef<any>(null);
  const editReplyEditorRef = useRef<any>(null); // for editing replies
  // Edit comment handler
  const handleEditComment = async (commentId: string) => {
    const content = editCommentEditorRef.current && editCommentEditorRef.current.editor ? editCommentEditorRef.current.editor.getContent() : '';
    if (!content.trim()) return;
    try {
      if (typeof addCommentMutation.editComment === 'function') {
        await addCommentMutation.editComment(commentId, content);
      } else if (addCommentMutation.editCommentMutation) {
        await addCommentMutation.editCommentMutation.mutateAsync({ commentId, text: content });
      } else if (addCommentMutation.editCommentFn) {
        await addCommentMutation.editCommentFn(commentId, content);
      }
      setEditingCommentId(null);
      if (editCommentEditorRef.current && editCommentEditorRef.current.editor) editCommentEditorRef.current.editor.setContent('');
      refetchComments();
      toast({ title: 'Comment updated', status: 'success', duration: 2000 });
    } catch (error: any) {
      toast({ title: 'Error editing comment', description: error.message || 'An error occurred', status: 'error', duration: 3000 });
    }
  };

  // Edit reply handler
  const handleEditReply = async (commentId: string, replyId: string) => {
    const content = editReplyEditorRef.current && editReplyEditorRef.current.editor ? editReplyEditorRef.current.editor.getContent() : '';
    if (!content.trim()) return;
    try {
      if (editReplyMutation) {
        await editReplyMutation.mutateAsync({ commentId, replyId, text: content });
        setEditingReplyId(null);
        if (editReplyEditorRef.current && editReplyEditorRef.current.editor) editReplyEditorRef.current.editor.setContent('');
        refetchComments();
        toast({ title: 'Reply updated', status: 'success', duration: 2000 });
      } else {
        toast({ title: 'Edit reply not available', status: 'warning', duration: 3000 });
      }
    } catch (error: any) {
      toast({ title: 'Error editing reply', description: error.message || 'An error occurred', status: 'error', duration: 3000 });
    }
  };
  // Removed fileInputRef, not needed for Cloudinary
  const toast = useToast();

  const handleAddComment = async () => {
    const content = mainCommentEditorRef.current && mainCommentEditorRef.current.editor ? mainCommentEditorRef.current.editor.getContent() : '';
    if (!content.trim() && commentImages.length === 0) return;
    try {
      await addCommentMutation.mutateAsync({ comment: content, images: commentImages });
      if (mainCommentEditorRef.current && mainCommentEditorRef.current.editor) mainCommentEditorRef.current.editor.setContent('');
      setCommentImages([]);
      setMainEditorContent(''); // Clear the state
      refetchComments();
      toast({ title: 'Comment added', status: 'success', duration: 2000 });
    } catch (error: any) {
      console.error('Error adding comment:', error);
      if (error.message?.includes('authentication') || error.response?.status === 401) {
        toast({ 
          title: 'Authentication Error', 
          description: 'Please log in again to continue',
          status: 'error', 
          duration: 4000 
        });
      } else {
        toast({ 
          title: 'Error adding comment', 
          description: error.message || 'An error occurred',
          status: 'error', 
          duration: 3000 
        });
      }
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    await deleteCommentMutation.mutateAsync(commentId);
    refetchComments();
    toast({ title: 'Comment deleted', status: 'info', duration: 2000 });
  };

  const handleAddReply = async (commentId: string) => {
    const ref = replyEditorRefs.current[commentId];
    const content = ref && ref.current && ref.current.editor ? ref.current.editor.getContent() : '';

    if (!content.trim() && replyImages.length === 0) return;
    if (!addReplyMutation) return;
    try {
      await addReplyMutation.mutateAsync({
        commentId,
        text: content,
        images: replyImages,
      });
      if (ref && ref.current && ref.current.editor) ref.current.editor.setContent('');
      setReplyImages([]);
      setReplyingTo(null);
      setReplyEditorContents(prev => ({
        ...prev,
        [commentId]: '',
      }));
      refetchComments();
      toast({ title: 'Reply added', status: 'success', duration: 2000 });
    } catch (error) {
      console.error('Error adding reply:', error);
      toast({ title: 'Error adding reply', status: 'error', duration: 2000 });
    }
  };

  const handleDeleteReply = async (commentId: string, replyId: string) => {
    if (!deleteReplyMutation) return;
    
    try {
      await deleteReplyMutation.mutateAsync({ commentId, replyId });
      refetchComments();
      toast({ title: 'Reply deleted', status: 'success', duration: 2000 });
    } catch (error) {
      toast({ title: 'Error deleting reply', status: 'error', duration: 2000 });
    }
  };

  // Cloudinary config (reuse from TaskDetails)
  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'all_files_uploads';
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dwnbctmwk';

  // TinyMCE refs for main comment and reply editors (already declared above)

  const renderReply = (reply: any, commentId: string) => {
    const replyTimestamp = formatCommentTimestamp(reply);
    return (
      <Box key={reply._id} ml={4}>
        <HStack align="start" bg="white" borderRadius="md" p={3} mb={2} boxShadow="sm">
          <Avatar
            size="xs"
            name={users[getAuthorId(reply.author)]?.firstName || 'User'}
            src={users[getAuthorId(reply.author)]?.avatarUrl}
            mr={2}
          />
          <Box flex="1">
            <Text fontWeight="bold" fontSize="xs">
              {users[getAuthorId(reply.author)]?.firstName} {users[getAuthorId(reply.author)]?.lastName}
              <Text as="span" color="gray.500" fontSize="xs" ml={2}>
                {replyTimestamp.label}
                {replyTimestamp.edited && (
                  <Text as="span" color="gray.400" ml={1}>(edited)</Text>
                )}
              </Text>
            </Text>
            {editingReplyId === reply._id ? (
              <Box>
                <Editor
                  key={`edit-reply-${reply._id}-${reply.updatedAt || reply.createdAt}`}
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl'}
                  ref={editReplyEditorRef}
                  initialValue={reply.text}
                  init={{
                    height: 180,
                    menubar: false,
                    plugins: [
                      'link', 'lists', 'code', 'autolink', 'charmap', 'table', 'paste', 'help', 'quickbars'
                    ],
                    toolbar:
                      'undo redo | bold italic underline | bullist numlist | link | code',
                    quickbars_insert_toolbar: false,
                    quickbars_selection_toolbar: 'bold italic underline | quicklink',
                    branding: false,
                    statusbar: false,
                    content_style: 'body { font-size:14px; }',
                  }}
                />
                <HStack spacing={2} mt={2}>
                  <IconButton
                    aria-label="Save"
                    icon={<CheckIcon />}
                    size="xs"
                    colorScheme="green"
                    onClick={() => handleEditReply(commentId, reply._id)}
                  />
                  <IconButton
                    aria-label="Cancel"
                    icon={<CloseIcon />}
                    size="xs"
                    colorScheme="gray"
                    onClick={() => {
                      setEditingReplyId(null);
                      if (editReplyEditorRef.current && editReplyEditorRef.current.editor) editReplyEditorRef.current.editor.setContent('');
                    }}
                  />
                </HStack>
              </Box>
            ) : (
              <>
                {/* Render reply with task mentions */}
                <TaskMentionDisplay
                  text={reply.text}
                  tasks={allTasks || []}
                  onTaskClick={(taskId) => window.open(`/dashboard/task/${taskId}`, '_blank')}
                  fontSize="sm"
                  mt={1}
                />
                {/* Show reply images inline */}
                {reply.images && reply.images.length > 0 && (
                  <VStack align="start" spacing={1} mt={2}>
                    {reply.images.map((url: string, idx: number) => (
                      <Box 
                        key={idx} 
                        cursor="pointer" 
                        _hover={{ opacity: 0.8, transform: 'scale(1.02)' }}
                        transition="all 0.2s"
                        onClick={() => handleImageClick(url)}
                      >
                        <img 
                          src={url} 
                          alt={`Reply image ${idx + 1}`} 
                          style={{ 
                            maxWidth: '200px', 
                            maxHeight: '200px', 
                            borderRadius: 8,
                            objectFit: 'cover',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }} 
                        />
                      </Box>
                    ))}
                  </VStack>
                )}
              </>
            )}
            {canPerformWrites && (
            <HStack mt={1} spacing={3}>
              <Tooltip 
                label={getLikeTooltip(reply.likedBy, 'like')}
                aria-label="Users who liked this reply"
                placement="top"
              >
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Like reply"
                    icon={<FaThumbsUp />}
                    size="xs"
                    variant="ghost"
                    isLoading={likeLoading[reply._id]}
                    onClick={() => handleLikeDislikeReply(commentId, reply._id, 'like')}
                    color={reply.likedBy?.some((id: string) => id === user?._id) ? 'green.500' : 'gray.400'}
                    bg={reply.likedBy?.some((id: string) => id === user?._id) ? 'green.50' : 'transparent'}
                    _hover={{
                      bg: reply.likedBy?.some((id: string) => id === user?._id) ? 'green.100' : 'gray.100',
                      color: 'green.600'
                    }}
                  />
                  <Text fontSize="xs" color={reply.likedBy?.length > 0 ? 'green.600' : 'gray.500'}>
                    {reply.likedBy?.length || 0}
                  </Text>
                </HStack>
              </Tooltip>
              <Tooltip 
                label={getLikeTooltip(reply.dislikedBy, 'dislike')}
                aria-label="Users who disliked this reply"
                placement="top"
              >
                <HStack spacing={1}>
                  <IconButton
                    aria-label="Dislike reply"
                    icon={<FaThumbsDown />}
                    size="xs"
                    variant="ghost"
                    isLoading={likeLoading[reply._id]}
                    onClick={() => handleLikeDislikeReply(commentId, reply._id, 'dislike')}
                    color={reply.dislikedBy?.some((id: string) => id === user?._id) ? 'red.500' : 'gray.400'}
                    bg={reply.dislikedBy?.some((id: string) => id === user?._id) ? 'red.50' : 'transparent'}
                    _hover={{
                      bg: reply.dislikedBy?.some((id: string) => id === user?._id) ? 'red.100' : 'gray.100',
                      color: 'red.600'
                    }}
                  />
                  <Text fontSize="xs" color={reply.dislikedBy?.length > 0 ? 'red.600' : 'gray.500'}>
                    {reply.dislikedBy?.length || 0}
                  </Text>
                </HStack>
              </Tooltip>
            </HStack>
            )}
          </Box>
          {canPerformWrites && user && isCommentAuthor(user._id, reply.author) && (
            <HStack>
              <IconButton
                aria-label="Edit reply"
                icon={<EditIcon />}
                size="xs"
                colorScheme="yellow"
                variant="ghost"
                onClick={() => {
                  setEditingReplyId(reply._id);
                }}
                mr={1}
              />
              <IconButton
                aria-label="Delete reply"
                icon={<DeleteIcon />}
                size="xs"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDeleteReply(commentId, reply._id)}
              />
            </HStack>
          )}
        </HStack>
      </Box>
    );
  };

  const renderComments = (comments: any[]) => {
    // Sort comments by createdAt (latest first)
    const sortedComments = [...comments].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    return sortedComments.map(comment => {
      const organizedReplies = organizeReplies(comment.replies || []);
      const commentTimestamp = formatCommentTimestamp(comment);
      
      return (
      <Box key={comment._id}>
        {/* Main Comment */}
        <HStack align="start" bg="gray.50" borderRadius="md" p={3} boxShadow="sm">
          <Avatar
            size="sm"
            name={users[getAuthorId(comment.author)]?.firstName || 'User'}
            src={users[getAuthorId(comment.author)]?.avatarUrl}
            mr={2}
          />
          <Box flex="1">
            <Text fontWeight="bold" fontSize="sm">
              {users[getAuthorId(comment.author)]?.firstName} {users[getAuthorId(comment.author)]?.lastName}
              <Text as="span" color="gray.500" fontSize="xs" ml={2}>
                {commentTimestamp.label}
                {commentTimestamp.edited && (
                  <Text as="span" color="gray.400" ml={1}>(edited)</Text>
                )}
              </Text>
            </Text>
            {editingCommentId === comment._id ? (
              <Box>
                <Editor
                  key={`edit-comment-${comment._id}-${comment.updatedAt || comment.createdAt}`}
                  apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl'}
                  ref={editCommentEditorRef}
                  initialValue={comment.text}
                  init={{
                    height: 180,
                    menubar: false,
                    plugins: [
                      'link', 'lists', 'code', 'autolink', 'charmap', 'table', 'paste', 'help', 'quickbars'
                    ],
                    toolbar:
                      'undo redo | bold italic underline | bullist numlist | link | code',
                    quickbars_insert_toolbar: false,
                    quickbars_selection_toolbar: 'bold italic underline | quicklink',
                    branding: false,
                    statusbar: false,
                    content_style: 'body { font-size:14px; }',
                  }}
                />
                <HStack spacing={2} mt={2}>
                  <IconButton
                    aria-label="Save"
                    icon={<CheckIcon />}
                    size="xs"
                    colorScheme="green"
                    onClick={() => handleEditComment(comment._id)}
                  />
                  <IconButton
                    aria-label="Cancel"
                    icon={<CloseIcon />}
                    size="xs"
                    colorScheme="gray"
                    onClick={() => {
                      setEditingCommentId(null);
                      if (editCommentEditorRef.current) editCommentEditorRef.current.setContent('');
                    }}
                  />
                </HStack>
              </Box>
            ) : (
              <TaskMentionDisplay
                text={comment.text}
                tasks={allTasks || []}
                onTaskClick={(taskId) => window.open(`/dashboard/task/${taskId}`, '_blank')}
                fontSize="sm"
                mt={1}
              />
            )}
            {/* Show comment images inline */}
            {comment.images && comment.images.length > 0 && (
              <VStack align="start" spacing={1} mt={2}>
                {comment.images.map((url: string, idx: number) => (
                  <Box 
                    key={idx} 
                    cursor="pointer" 
                    _hover={{ opacity: 0.8, transform: 'scale(1.02)' }}
                    transition="all 0.2s"
                    onClick={() => handleImageClick(url)}
                  >
                    <img 
                      src={url} 
                      alt={`Comment image ${idx + 1}`} 
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '200px', 
                        borderRadius: 8,
                        objectFit: 'cover',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }} 
                    />
                  </Box>
                ))}
              </VStack>
            )}
            {canPerformWrites && (
            <HStack mt={2} spacing={4}>
              <HStack>
                <Tooltip 
                  label={getLikeTooltip(comment.likedBy, 'like')}
                  aria-label="Users who liked this comment"
                  placement="top"
                >
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="Like"
                      icon={<FaThumbsUp />}
                      size="xs"
                      variant="ghost"
                      isLoading={likeLoading[comment._id]}
                      onClick={() => handleLikeDislikeComment(comment._id, 'like')}
                      color={comment.likedBy?.some((id: string) => id === user?._id) ? 'green.500' : 'gray.400'}
                      bg={comment.likedBy?.some((id: string) => id === user?._id) ? 'green.50' : 'transparent'}
                      _hover={{
                        bg: comment.likedBy?.some((id: string) => id === user?._id) ? 'green.100' : 'gray.100',
                        color: 'green.600'
                      }}
                    />
                    <Text fontSize="xs" color={comment.likedBy?.length > 0 ? 'green.600' : 'gray.500'}>
                      {comment.likedBy?.length || 0}
                    </Text>
                  </HStack>
                </Tooltip>
                <Tooltip 
                  label={getLikeTooltip(comment.dislikedBy, 'dislike')}
                  aria-label="Users who disliked this comment"
                  placement="top"
                >
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="Dislike"
                      icon={<FaThumbsDown />}
                      size="xs"
                      variant="ghost"
                      isLoading={likeLoading[comment._id]}
                      onClick={() => handleLikeDislikeComment(comment._id, 'dislike')}
                      color={comment.dislikedBy?.some((id: string) => id === user?._id) ? 'red.500' : 'gray.400'}
                      bg={comment.dislikedBy?.some((id: string) => id === user?._id) ? 'red.50' : 'transparent'}
                      _hover={{
                        bg: comment.dislikedBy?.some((id: string) => id === user?._id) ? 'red.100' : 'gray.100',
                        color: 'red.600'
                      }}
                    />
                    <Text fontSize="xs" color={comment.dislikedBy?.length > 0 ? 'red.600' : 'gray.500'}>
                      {comment.dislikedBy?.length || 0}
                    </Text>
                  </HStack>
                </Tooltip>
              </HStack>
              {addReplyMutation && (
                <Button 
                  variant="ghost" 
                  size="xs" 
                  color="blue.600"
                  onClick={() => {
                    setReplyingTo(replyingTo === comment._id ? null : comment._id);
                  }}
                >
                  Reply
                </Button>
              )}
            </HStack>
            )}
          </Box>
          {canPerformWrites && user && isCommentAuthor(user._id, comment.author) && (
            <HStack>
              <IconButton
                aria-label="Edit comment"
                icon={<EditIcon />}
                size="sm"
                colorScheme="yellow"
                variant="ghost"
                onClick={() => {
                  setEditingCommentId(comment._id);
                }}
                mr={1}
              />
              <IconButton
                aria-label="Delete comment"
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                variant="ghost"
                onClick={() => handleDeleteComment(comment._id)}
              />
            </HStack>
          )}
        </HStack>

        {/* Reply Input Box for comment - show immediately below main comment */}
        {canPerformWrites && replyingTo === comment._id && (
          <Box ml={2} mt={2} p={3} bg="gray.50" borderRadius="md" borderLeftWidth={4} borderLeftColor={userColor(user?._id)}>
            <VStack spacing={2}>
              {(() => {
                if (!replyEditorRefs.current[comment._id]) {
                  replyEditorRefs.current[comment._id] = React.createRef();
                }
                return null;
              })()}
              <Editor
                apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl'}
                ref={(() => {
                  if (!replyEditorRefs.current[comment._id]) {
                    replyEditorRefs.current[comment._id] = React.createRef();
                  }
                  return replyEditorRefs.current[comment._id];
                })()}
                initialValue={''}
                onEditorChange={(content) => {
                  setReplyEditorContents(prev => ({ ...prev, [comment._id]: content }));
                }}
                init={{
                  height: 180,
                  menubar: false,
                  plugins: [
                    'link', 'lists', 'code', 'autolink', 'charmap', 'table', 'paste', 'help', 'quickbars'
                  ],
                  toolbar:
                    'undo redo | bold italic underline | bullist numlist | link | code',
                  quickbars_insert_toolbar: false,
                  quickbars_selection_toolbar: 'bold italic underline | quicklink',
                  branding: false,
                  statusbar: false,
                  content_style: 'body { font-size:14px; }',
                }}
              />
              <Button
                leftIcon={<AttachmentIcon />}
                colorScheme="gray"
                variant="outline"
                size="xs"
                cursor="pointer"
                mb={replyImages.length > 0 ? 2 : 0}
                onClick={() => {
                  window.cloudinary.openUploadWidget(
                    {
                      cloudName: CLOUDINARY_CLOUD_NAME,
                      uploadPreset: CLOUDINARY_UPLOAD_PRESET,
                      sources: ['local', 'url', 'camera'],
                      multiple: true,
                      resourceType: 'image',
                    },
                    (error: any, result: any) => {
                      if (!error && result.event === 'success') {
                        setReplyImages(prev => [...prev, result.info.secure_url]);
                      }
                    }
                  );
                }}
              >
                Attach images
              </Button>
              {replyImages.length > 0 && (
                <VStack align="start" spacing={1} mt={2} maxH="60px" overflowY="auto">
                  {replyImages.map((url, idx) => (
                    <HStack key={idx} spacing={2}>
                      <AttachmentIcon color="gray.500" />
                      <Text fontSize="xs" noOfLines={1} maxW="100px">Image {idx + 1}</Text>
                      <IconButton
                        aria-label="Remove image"
                        icon={<DeleteIcon />}
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => setReplyImages(imgs => imgs.filter((_, i) => i !== idx))}
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
              <HStack spacing={2} alignSelf="flex-end">
                <Button 
                  size="xs" 
                  variant="ghost"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyImages([]);
                    setReplyEditorContents(prev => ({ ...prev, [comment._id]: '' }));
                    const ref = replyEditorRefs.current[comment._id];
                    if (ref && ref.current && ref.current.editor) ref.current.editor.setContent('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  size="xs" 
                  colorScheme="blue"
                  onClick={() => handleAddReply(comment._id)}
                  isDisabled={(() => {
                    const content = replyEditorContents[comment._id] || '';
                    const hasText = content.replace(/<[^>]*>/g, '').trim().length > 0;
                    const hasImages = replyImages.length > 0;
                    return !hasText && !hasImages;
                  })()}
                >
                  Reply
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* Replies Section */}
        {organizedReplies && organizedReplies.length > 0 && (
          <Box ml={8} mt={2} borderLeft="2px" borderColor="gray.200" pl={4}>
            {organizedReplies.map((reply: any) => renderReply(reply, comment._id))}
          </Box>
        )}
      </Box>
      );
    });
  };

  return (
    <Box>
      {canPerformWrites && (
        <>
      {/* Main comment box always at the top */}
      <HStack mb={4} align="start">
        <Box flex="1">
          <Editor
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl'}
            ref={mainCommentEditorRef}
            initialValue={''}
            onEditorChange={(content) => {
              setMainEditorContent(content);
            }}
            init={createEditorSetup()}
          />
        </Box>
      </HStack>
      {/* Cloudinary image upload for comments */}
      <Box mb={4}>
        <HStack align="end" spacing={4} mb={10}>
          <Box flex="1">
            <Button
              leftIcon={<AttachmentIcon />}
              colorScheme="gray"
              variant="outline"
              size="sm"
              cursor="pointer"
              mb={commentImages.length > 0 ? 2 : 0}
              onClick={() => {
                window.cloudinary.openUploadWidget(
                  {
                    cloudName: CLOUDINARY_CLOUD_NAME,
                    uploadPreset: CLOUDINARY_UPLOAD_PRESET,
                    sources: ['local', 'url', 'camera'],
                    multiple: true,
                    resourceType: 'image',
                  },
                  (error: any, result: any) => {
                    if (!error && result.event === 'success') {
                      setCommentImages(prev => [...prev, result.info.secure_url]);
                    }
                  }
                );
              }}
            >
              Attach images
            </Button>
            {commentImages.length > 0 && (
              <VStack align="start" spacing={1} mt={2} maxH="80px" overflowY="auto">
                {commentImages.map((url, idx) => (
                  <HStack key={idx} spacing={2}>
                    <AttachmentIcon color="gray.500" />
                    <Text fontSize="xs" noOfLines={1} maxW="120px">Image {idx + 1}</Text>
                    <IconButton
                      aria-label="Remove image"
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => setCommentImages(imgs => imgs.filter((_, i) => i !== idx))}
                    />
                  </HStack>
                ))}
              </VStack>
            )}
          </Box>
          <Button
            onClick={handleAddComment}
            colorScheme="blue"
            size="md"
            px={6}
            isDisabled={(() => {
              const hasText = mainEditorContent.replace(/<[^>]*>/g, '').trim().length > 0;
              const hasImages = commentImages.length > 0;
              return !hasText && !hasImages;
            })()}
            boxShadow="md"
          >
            Add Comment
          </Button>
        </HStack>
      </Box>
        </>
      )}

      <Box my={6}>
        <Box borderBottom="1px solid" borderColor="gray.200" />
      </Box>

      {/* Comments list below */}
      <VStack align="stretch" spacing={3} mb={4}>
        {comments.length === 0 && (
          <Text color="gray.400" textAlign="center">
            No comments yet.
          </Text>
        )}
        {renderComments(comments)}
      </VStack>

      {/* Image Preview Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent 
          bg="transparent" 
          boxShadow="none" 
          maxW="95vw" 
          maxH="95vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow={"hidden"}
          borderRadius="md"
        >
          <ModalCloseButton 
            color="white" 
            bg="orange.600" 
            boxShadow={'md'}
            _hover={{ bg: "blackAlpha.800" }}
            size="lg"
            zIndex={10}
            position="absolute"
            top={4}
            right={4}
            borderRadius="full"
          />
          <ModalBody 
            display="block" 
            alignItems="center" 
            justifyContent="center" 
            p={4}
            w="100%"
            h="100%"
            padding={6}
            // overflowY={'auto'}
          >
            {previewImage && (
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="center"
                w="100%"
                h="100%"
              >
                <img 
                  src={previewImage} 
                  alt="Preview" 
                  style={{ 
                    // maxWidth: '100%', 
                    // maxHeight: '100%', 
                    width: '100vh',
                    height: 'auto',
                    // objectFit: 'contain',
                    borderRadius: 8,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
                  }} 
                />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CommentsSection;
